import {AnyAction} from 'redux';
import {
  all,
  call,
  delay,
  put,
  SagaReturnType,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {tt} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {isTrue} from '../../../../../../common/shared/utils/isTrue';
import {TalkViewerErrorType} from '../../../../../../errors/TalkViewerErrorType.enum';
import {
  TalkViewerData,
  TalkViewerType,
} from '../../../../../../graphql/generated/types';
import {
  ANIMATION_DURATION,
  CLOSE_TALK_VIEWER_ERROR_TYPE_LIST,
  TALK_VIEWER_ERROR_MESSAGE_MAP,
  TYPEWRITE_EFFECT_SPEED,
} from '../constant/talkViewerConstant';
import {events} from '../constant/talkViewerEvents';
import {TalkViewerState} from '../state/TalkViewerState.interface';
import {
  appendAllExtraTalks,
  appendTalk,
  changeScene,
  changeTalkGroup,
  changeViewingMode,
  clearTalkViewerError,
  forceProcessToTalkIndex,
  hideFullscreen,
  hideTalkReplayBox,
  pauseAutoTap,
  playAutoTap,
  playMedia,
  registerBgm,
  replayTalk,
  setAutoTapSpeed,
  setExposedTalk,
  setIsMediaAutoPlay,
  setIsSettingMenuExposed,
  setIsTutorialExposed,
  setMuted,
  setNeedMediaPlayedDirect,
  setPreloadedMedia,
  setPreviewEpisodeTitle,
  setPreviewTitle,
  setVideoPlayType,
  setViewerMenuHeight,
  setVisitedTalkCountToViewerEnd,
  showFullscreen,
  stopAutoTap,
  stopMedia,
  talkViewerErrorOccured,
  talkViewerInfoFetched,
  talkViewerInfoLoadingFinished,
  unregisterBgm,
} from '../state/talkViewerSlice';
import {ViewerEndState} from '../state/ViewerEndState.intreface';
import {ViewerInfoState} from '../state/ViewerInfoState.interface';
import {setIsViewerEndExposed} from '../state/viewerEndSlice';
import {setNonstopWatching} from '../state/viewerInfoSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {AutoTapSpeed} from '../type/AutoTapSpeed.enum';
import {AutoTapType} from '../type/AutoTapType.enum';
import {PreloadType} from '../type/PreloadType.enum';
import {TalkDataType} from '../type/TalkDataType.enum';
import {TalkEffectType} from '../type/TalkEffectType.enum';
import {VideoPlayType} from '../type/VIdeoPlayType.enum';
import {ViewingMode} from '../type/ViewingMode.enum';
import {fetchTalkViewerContent} from './fetchTalkViewerContent.thunk';
import {fetchTalkViewerContentFromAdminSite} from './fetchTalkViewerContentFromAdminSite.thunk';
import {fetchTalkViewerContentFromCms} from './fetchTalkViewerContentFromCms.thunk';
import {getPreloadedMedia} from './getPreloadedMedia';
import {getTalkViewerEndPageCount} from './getTalkViewerEndPageCount.saga';
import {getVideoPlayType} from './getVideoPlayType';
import {processNextStep} from './processNextStep.saga';
import {restoreFromLastViewRate} from './restoreFromLastViewRate';
import {
  selectCanShowViewerEnd,
  selectTalkViewerState,
  selectViewerEndState,
  selectViewerInfoState,
} from './selector';
import {stopAllMedia} from './stopAllMedia.saga';
import {talkEventFired} from './talkEventFired.saga';

export function* fetchTalkViewerContentFulfilledWorker({
  meta,
  payload,
}: ReturnType<typeof fetchTalkViewerContent.fulfilled>) {
  yield put(
    talkViewerInfoFetched({
      productId: meta.arg.productId,
      triggerType: meta.arg.triggerType,
      response: payload.response,
      viewerInfoData: payload.viewerInfoData,
    })
  );
}

export function* fetchTalkViewerContentFromAdminSiteFulfilledWorker({
  payload,
  meta,
}: ReturnType<typeof fetchTalkViewerContentFromAdminSite.fulfilled>) {
  const {serviceEpisodeUid} = meta.arg;
  try {
    const {preview: previewData, episodeTitle, title, viewerInfoData} = payload;
    yield put(setPreviewEpisodeTitle(episodeTitle));
    yield put(setPreviewTitle(title));
    yield put(changeViewingMode(ViewingMode.Scroll));
    yield put(setMuted(false));
    yield put(
      talkViewerInfoFetched({
        productId: serviceEpisodeUid,
        triggerType: 'page_move',
        response: previewData,
        viewerInfoData,
      })
    );
  } catch (error) {
    console.error(
      'fetchTalkViewerInfoTrigerredFromAdminSite saga failed',
      error
    );
    yield put(talkViewerErrorOccured(TalkViewerErrorType.UnknownError));
  }
}

export function* fetchTalkViewerContentFromCmsFulfilledWorker({
  payload,
  meta,
}: ReturnType<typeof fetchTalkViewerContentFromCms.fulfilled>) {
  try {
    const {episodeUid} = meta.arg;
    yield put(
      talkViewerInfoFetched({
        productId: episodeUid,
        triggerType: 'page_move',
        response: payload.response,
        viewerInfoData: payload.viewerInfoData,
      })
    );
  } catch (error) {
    console.error(
      'fetchTalkViewerInfoTrigerredFromAdminSite saga failed',
      error
    );
    yield put(talkViewerErrorOccured(TalkViewerErrorType.UnknownError));
  }
}

export function* talkViewerInfoFetchedWorker({
  payload: {productId, triggerType, viewerInfoData},
}: ReturnType<typeof talkViewerInfoFetched>) {
  // 앱에 저장된 데이터가 반영되지 않은 초기 katalk novel state
  const initialTalkViewerState: TalkViewerState = yield select(
    selectTalkViewerState
  );
  const viewerInfoState: ViewerInfoState = yield select(selectViewerInfoState);
  const isTapOnlyTalkViewer =
    (viewerInfoData?.viewerData as TalkViewerData)?.talkDownloadData
      ?.talkViewerType === TalkViewerType.Tap;
  const {isMobile, isWebView, isIos} = requestContext.requestInfo.display;

  yield put(
    setVideoPlayType(
      getVideoPlayType({
        userAgentStr: navigator.userAgent,
        isWebView,
      })
    )
  );

  // 오토탭은 새로 접근 시 항상 꺼진 상태
  yield put(stopAutoTap());

  const isPcPreview = initialTalkViewerState.isPreview && !isMobile;

  // 웹뷰가 아닌 경우는 모든 미디어를 컴포넌트에서 직접 재생해준다(모웹 정책때문).
  // 단, PC 저작툴은 예외(자동재생이 되지 않으면 불편한 부분이 많다고 한다)
  if (!isWebView && !isPcPreview) {
    yield put(setNeedMediaPlayedDirect(true));
  }

  if (window?.KNApp?.setIsTapOnlyProduct) {
    window.KNApp.setIsTapOnlyProduct(isTapOnlyTalkViewer);
  }
  const isNonstopWatching =
    !!viewerInfoState.currentSeriesId &&
    !!viewerInfoState.nonstopWatchingInSeries[viewerInfoState.currentSeriesId]
      ?.isNonstopWatching;
  if (window.KPApp?.getIsNonstopWatching) {
    const isNonstopWatchingApp = isTrue(window.KPApp.getIsNonstopWatching());
    if (
      viewerInfoState.currentSeriesId &&
      isNonstopWatchingApp !== isNonstopWatching
    ) {
      yield put(
        setNonstopWatching({
          seriesId: viewerInfoState.currentSeriesId,
          isNonstopWatching: isNonstopWatchingApp,
        })
      );
    }
  }

  // 탭 전용 작품이면 앱에 저장된 값과 상관 없이 항상 탭모드로 변경시킨다.
  if (isTapOnlyTalkViewer) {
    yield put(changeViewingMode(ViewingMode.Tap));
  } else if (window?.KNApp?.getViewingMode) {
    try {
      const viewingMode = window.KNApp.getViewingMode() as ViewingMode;
      if (Object.values(ViewingMode).includes(viewingMode)) {
        yield put(changeViewingMode(viewingMode));
      } else {
        throw new Error(
          `KNApp.getViewingMode result is invalid ViewingMode : ${viewingMode}`
        );
      }
    } catch (err) {
      console.error('talkViewerInfoFetched KNApp.getViewingMode', err);
    }
  }
  if (window?.KNApp?.getViewerMenuHeight) {
    try {
      const getViewerMenuHeight = JSON.parse(
        window.KNApp.getViewerMenuHeight()
      );
      if (hasViewerMenuHeight(getViewerMenuHeight)) {
        yield put(setViewerMenuHeight(getViewerMenuHeight));
      } else {
        throw new Error(
          `KNApp.getViewerMenuHeight result is invalid : ${getViewerMenuHeight}`
        );
      }

      function hasViewerMenuHeight(viewerMenuHeight: any) {
        return (
          typeof viewerMenuHeight.top === 'number' &&
          typeof viewerMenuHeight.bottom === 'number' &&
          (viewerMenuHeight.safeAreaTop === undefined ||
            typeof viewerMenuHeight.safeAreaTop === 'number') &&
          (viewerMenuHeight.safeAreaBottom === undefined ||
            typeof viewerMenuHeight.safeAreaBottom === 'number')
        );
      }
    } catch (err) {
      console.error('talkViewerInfoFetched KNApp.getViewerMenuHeight', err);
    }
  }
  if (window?.KNApp?.getIsSettingMenuExposed) {
    try {
      const isSettingMenuExposed = JSON.parse(
        window.KNApp.getIsSettingMenuExposed()
      );
      if (typeof isSettingMenuExposed === 'boolean') {
        yield put(setIsSettingMenuExposed(isSettingMenuExposed));
      } else {
        throw new Error(
          `KNApp.getIsSettingMenuExposed result is not boolean : ${isSettingMenuExposed}`
        );
      }
    } catch (err) {
      console.error('talkViewerInfoFetched KNApp.getIsSettingMenuExposed', err);
    }
  }

  if (window?.KNApp?.getIsMediaAutoPlay) {
    try {
      const mediaAutoPlay = JSON.parse(window.KNApp.getIsMediaAutoPlay());
      if (typeof mediaAutoPlay === 'boolean') {
        yield put(setIsMediaAutoPlay(mediaAutoPlay));
      } else {
        throw new Error(
          `KNApp.getIsMediaAutoPlay result is not boolean : ${mediaAutoPlay}`
        );
      }
    } catch (err) {
      console.error('talkViewerInfoFetched KNApp.getIsMediaAutoPlay', err);
    }
  }
  // PC 미리보기는 항상 자동재생 on
  else if (isPcPreview) {
    yield put(setIsMediaAutoPlay(true));
  }
  if (window?.KNApp?.getMuted) {
    try {
      const muted = JSON.parse(window.KNApp.getMuted());
      if (typeof muted === 'boolean') {
        yield put(setMuted(muted));
      } else {
        throw new Error(`KNApp.getMuted result is not boolean : ${muted}`);
      }
    } catch (err) {
      console.error('talkViewerInfoFetched KNApp.getMuted', err);
    }
  }

  let initialTalkIndex =
    restoreFromLastViewRate({
      rate: viewerInfoData?.item.serviceProperty?.lastViewInfo?.rate || 0,
      total:
        initialTalkViewerState.productInfoMap[productId].progress
          .totalTalkCount,
    }) || 1; // 만약 읽은 적 없는 경우(rate 가 0인 경우) 첫번째 톡 부터 진행한다

  // 뷰어엔드인 경우 초기톡은 totalCount + 1이 톡뷰어 스펙입니다.
  if (viewerInfoData?.item?.serviceProperty?.lastViewInfo?.spineIndex === 1) {
    initialTalkIndex =
      initialTalkViewerState.productInfoMap[productId].progress.totalTalkCount +
      1;
  }

  const isFromPrevProduct = isWebView
    ? isTrue(window?.KNApp?.getIsFromPrevProduct?.())
    : viewerInfoState.isFromPrevProduct;

  if (triggerType === 'page_move' && isFromPrevProduct) {
    // 이전작품에서 다음작품으로 가기를 통해 넘어온 경우는 무조건 처음부터 시작합니다.
    initialTalkIndex = 1;
  } else {
    const productInfo = initialTalkViewerState.productInfoMap[productId];
    if (productInfo?.progress.visitedTalkCount) {
      initialTalkIndex = productInfo.progress.visitedTalkCount;
    }
  }

  if (initialTalkViewerState.settings.preloadType !== PreloadType.None) {
    const preloadedMedia: SagaReturnType<typeof getPreloadedMedia> =
      yield getPreloadedMedia({
        preloadType: initialTalkViewerState.settings.preloadType,
        productInfoMap: initialTalkViewerState.productInfoMap,
        sceneMap: initialTalkViewerState.sceneMap,
        talkGroupMap: initialTalkViewerState.talkGroupMap,
        talkMap: initialTalkViewerState.talkMap,
        productId,
        isIos,
        currentTalkIndex: initialTalkIndex,
      });
    yield put(setPreloadedMedia(preloadedMedia));
  }

  yield put(forceProcessToTalkIndex(initialTalkIndex));

  // 뷰어 로딩이 내려간다. 즉 뷰어에 내용이 노출되기 시작하므로 이 코드 아래로는 뷰어 로딩이 종료 된 이후 처리 될 코드들이 들어간다.
  yield put(talkViewerInfoLoadingFinished());

  // 앱에 저장된 autoTapSpeed가 0이 아닌 경우에는 autoTap을 이어서 해 준다.
  if (window?.KNApp?.getAutoTapSpeed) {
    try {
      const autoTapSpeed = JSON.parse(window.KNApp.getAutoTapSpeed());
      if (typeof autoTapSpeed === 'number') {
        const _autoTapSpeed = autoTapSpeed as AutoTapSpeed;
        if (
          _autoTapSpeed === AutoTapSpeed.Fast ||
          _autoTapSpeed === AutoTapSpeed.Slow ||
          _autoTapSpeed === AutoTapSpeed.Normal
        ) {
          // 오토탭이 시작되면 바로 다음톡을 진행 해 버리므로, 뷰어 로딩 직후 오토탭을 시작하는 경우에는 현재 톡 -1을 해 줘 오토탭으로 현재 톡 부터 진행할 수 있게 해 준다.
          yield put(forceProcessToTalkIndex(Math.max(0, initialTalkIndex - 1)));
          yield put(setAutoTapSpeed(_autoTapSpeed));
          yield put(playAutoTap());
          // 저장된 값 불러온 뒤 초기화
          window?.KNApp?.setAutoTapSpeed?.(AutoTapSpeed.None);
        }
      } else {
        throw new Error(
          `KNApp.getAutoTapSpeed result is not number : ${autoTapSpeed}`
        );
      }
    } catch (err) {
      console.error('talkViewerInfoFetched KNApp.getAutoTapSpeed', err);
    }
  } else if (
    isNonstopWatching &&
    initialTalkViewerState.internalSettings.autoTap === AutoTapType.Pause
  ) {
    yield put(forceProcessToTalkIndex(Math.max(0, initialTalkIndex - 1)));
    yield put(playAutoTap());
  }

  // 앱 저장분이 모두 반영 된 katalk novel state
  const talkViewerStateApplyingAppData: TalkViewerState = yield select(
    selectTalkViewerState
  );
  // 오토탭 중이 아닌 경우 탭모드로 뷰어 첫 진입시에는 초기 톡의 톡 이벤트를 실행 해 준다. (ex. 풀스크린 동영상이면 뷰어 로딩 종료 직후 풀스크린 동영상이 바로 나온다.)
  if (
    talkViewerStateApplyingAppData.internalSettings.autoTap !== AutoTapType.Play
  ) {
    const targetTalkId = Object.keys(
      talkViewerStateApplyingAppData.talkMap
    ).find(
      talkId =>
        talkViewerStateApplyingAppData.talkMap[talkId].talkIndex ===
          initialTalkIndex &&
        talkViewerStateApplyingAppData.talkMap[talkId].productId ===
          talkViewerStateApplyingAppData.currentProductId
    );
    if (
      targetTalkId &&
      talkViewerStateApplyingAppData.settings.viewingMode === ViewingMode.Tap
    ) {
      yield call(talkEventFired, targetTalkId, true);
    }
  }
}

// eslint-disable-next-line require-yield
export function* talkViewerInfoLoadingFinishedWorker() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  // 로딩이 정상적으로 끝난 뒤에는 존재하는 에러페이지를 지워준다
  if (talkViewerState.error) {
    yield put(clearTalkViewerError());
  }

  const viewerInfoState: ViewerInfoState = yield select(selectViewerInfoState);
  const isFromPrevProduct = viewerInfoState.isFromPrevProduct;
  // 로딩 후에 튜토리얼이 떠 있거나 이전 작품에서 넘어온 상황이 아니면 뷰어메뉴가 뜬 상태로 시작한다.
  if (!talkViewerState.isTutorialExposed && !isFromPrevProduct) {
    yield put(setIsViewerMenuExposed({isExposed: true}));
  }

  window?.KNApp?.finishViewerLoading?.();
}

export const AUTO_TAP_BASE_SPEED_MS = 1000; // 1초
export function* playAutoTapWorker() {
  window?.KNApp?.autoTapPlayed?.();
  yield put(setIsViewerMenuExposed({isExposed: false}));
  while (true) {
    const talkViewerState: TalkViewerState = yield select(
      selectTalkViewerState
    );
    const {
      internalSettings: {autoTap, autoSpeed, muted},
      settings: {mediaAutoPlay},
      talkMap,
    } = talkViewerState;
    if (autoTap !== AutoTapType.Play) {
      return;
    }
    const duration = AUTO_TAP_BASE_SPEED_MS * autoSpeed;
    const appendedTalkId: SagaReturnType<typeof processNextStep> = yield call(
      processNextStep,
      talkViewerState
    );
    if (!appendedTalkId) {
      return;
    }
    const talk = talkMap[appendedTalkId];
    // 효과음/동영상 톡일 때 미디어 오토플레이 상태면 해당 톡 미디어 중지 액션이 올 때까지 멈춘다
    if (
      talk?.media &&
      mediaAutoPlay &&
      // muted 상태면 audio를 재생하지 않으므로 이 경우는 제외한다.
      ((talk.datatype === TalkDataType.Sound && !muted) ||
        talk.datatype === TalkDataType.Movie ||
        talk.datatype === TalkDataType.FullscreenVideo)
    ) {
      yield take((action: AnyAction) => {
        return action.type === stopMedia.type && action.payload === talk.id;
      });
    }
    // 글효과의 경우도 글효과가 끝나는 걸 확인 후 오토탭이 넘어가기로 결정되어서 여기에 관련 코드를 추가했다.
    if (talk?.datatype === TalkDataType.TalkEffect && talk?.effect) {
      // 타이핑 글효과의 경우는 css 애니메이션을 사용하지 않고 setTimeout을 사용해 특정 시간마다 글자가 한개씩 추가되도록 구현되었으므로 글자수 * 노출시간만큼 delay 후 진행한다.
      if (talk.effect === TalkEffectType.Typing) {
        yield delay(talk.scripts.length * TYPEWRITE_EFFECT_SPEED);
      }
      // 그 외 글효과는 css 애니메이션 시간만큼 대기
      else {
        yield delay(ANIMATION_DURATION[talk.effect] * 1000);
      }
    }
    yield delay(duration);
  }
}

export function* appendTalkWorker({payload}: ReturnType<typeof appendTalk>) {
  const talkId = payload;
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const talk = talkViewerState.talkMap[talkId];
  if (!talk) {
    return;
  }

  const isNextTalkFullscreen =
    talk.datatype === TalkDataType.FullscreenImage ||
    (talk.datatype === TalkDataType.FullscreenVideo &&
      // 풀스크린 비디오는 mediaAutoPlay가 아니면 톡 진행 중 fullscreen이 뜨지 않는다.
      talkViewerState.settings.mediaAutoPlay);
  /**
   * append 된 톡이 풀스크린이 아닌 톡인데 현재 풀스크린 상태라면 여기서 풀스크린을 닫아준다. 기존에는 appendTalk을 실행해주는 processNextStep 함수에서 풀스크린을 닫아주고 넘어왔는데
   * appendTalk이 진행된 후 닫도록 변경한 이유는, 풀스크린 이미지가 닫힐 때 애니메이션이 있어서 풀스크린 이미지 -> 풀스크린 이미지로 넘어갈 때 불필요한 애니메이션이 발생할 수 있기 떄문.
   * 풀스크린 -> 풀스크린으로 톡이 진행 된 경우는 풀스크린을 닫는 이벤트를 실행하지 않고 풀스크린 정보만 바꿔주는 게 목적이다.
   */
  if (talkViewerState.fullscreen && !isNextTalkFullscreen) {
    yield put(events.fullscreenHideRequested());
  }
  if (talkViewerState.settings.viewingMode === ViewingMode.Tap) {
    window.KNApp?.concealScrollBar?.();
    // 스크롤모드에서는 talkAppend 사용하지 않음.
  } else {
    return;
  }

  const {currentProductId, productInfoMap} = talkViewerState;
  if (!currentProductId) {
    return;
  }
  // 톡이 추가 될 때 자동으로 해당 톡에 포함 된 이벤트를 발생시킨다.
  yield call(talkEventFired, talkId, true);
  const product = productInfoMap[currentProductId];
  const endPageCount: number = yield call(getTalkViewerEndPageCount);

  window?.KNApp?.setProgress?.(
    Math.min(
      product.progress.visitedTalkCount,
      product.progress.totalTalkCount
    ), // @ts-ignore TODO: 기존 app handler는 param 여러개가 넘어갔던 것 같은데, 지금타입은 막혀있어서 확인 필요
    product.progress.totalTalkCount,
    endPageCount
  );
}

export function* changeViewingModeWorker({
  payload,
}: ReturnType<typeof changeViewingMode>) {
  const viewingMode = payload;
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const viewerEndState: ViewerEndState = yield select(selectViewerEndState);
  if (talkViewerState.shownReplay) {
    yield put(hideTalkReplayBox());
  }
  if (talkViewerState.bgm) {
    yield put(unregisterBgm());
  }
  if (viewingMode === ViewingMode.Scroll) {
    window.KNApp?.exposeScrollBar?.();
    yield put(appendAllExtraTalks());

    if (talkViewerState.internalSettings.autoTap !== AutoTapType.Stop) {
      yield put(stopAutoTap());
    }
    if (talkViewerState.fullscreen) {
      yield put(hideFullscreen());
    }
    yield call(stopAllMedia);
  } else {
    window.KNApp?.concealScrollBar?.();
    const {currentProductId, productInfoMap}: TalkViewerState = yield select(
      selectTalkViewerState
    );
    if (!currentProductId) {
      return;
    }
    const productInfo = productInfoMap[currentProductId];
    if (
      !productInfo ||
      !productInfo.exposed ||
      !productInfo.exposed.bottomTalkId ||
      viewerEndState.isExposed
    ) {
      return;
    }
    const lastVisibleTalkId =
      productInfo.exposed && productInfo.exposed.bottomTalkId;
    yield put(events.forceProcessToTalkId(lastVisibleTalkId));
  }
}

export function* replayTalkWorker({payload}: ReturnType<typeof replayTalk>) {
  const talkId = payload;
  yield call(stopAllMedia);
  yield put(events.forceProcessToTalkId(talkId));
  yield call(talkEventFired, talkId, true);
}

export function* forceProcessToTalkIndexWorker({
  payload,
}: ReturnType<typeof forceProcessToTalkIndex>) {
  // 특정 톡 이동은 모든 미디어를 중지시킨다
  yield call(stopAllMedia);
  yield put(hideFullscreen());
  const receivedTalkIndex = payload;
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const viewerEndState: ViewerEndState = yield select(selectViewerEndState);
  const currentProductId = talkViewerState.currentProductId;
  if (!currentProductId) {
    return;
  }
  if (talkViewerState.shownReplay) {
    yield put(hideTalkReplayBox());
  }
  const progress = talkViewerState.productInfoMap[currentProductId].progress;

  const talkIndex =
    progress.totalTalkCount < receivedTalkIndex
      ? progress.totalTalkCount
      : receivedTalkIndex;

  const canShowViewerEnd: boolean = yield select(state =>
    selectCanShowViewerEnd(state)
  );

  // totalTalkCount 보다 큰 값이 들어오면 뷰어엔드를 보여주기로 App 과 협의함. (정주행 여부나 다음편 존재 여부에 따라 다름)
  const isViewerEndIndex =
    canShowViewerEnd && progress.totalTalkCount < receivedTalkIndex;

  // 뷰어엔드 인덱스면 뷰어엔드 띄우기, 톡 인덱스면 뷰어엔드 내리기
  if (isViewerEndIndex) {
    /**
     * 뷰어엔드 인덱스면 따로 setProgress를 호출 해 주지 않는다. 필요한 경우는 setIsViewerEndExposed에서 마지막 톡으로 setProgress 호출
     * ios 앱에서는 뷰어엔드 노출 여부에 따라 seekbar를 조작 해 주는데, isViewerEndExposed 값이 true면 항상 seekbar가 맨 끝에 있는다.
     * 그런데 setProgress -> setIsViewerEndExposed 순으로 부르면 잠시 해당 톡으로 이동했다가 seekbar 맨 끝으로 이동하면서 버벅 거리는 느낌이 난다.
     * 그래서 ios 앱과 협의해서 setIsViewerEndExposed 호출 전에는 setProgress를 호출하지 않기로 함
     */
    if (!viewerEndState.isExposed) {
      yield put(setIsViewerEndExposed(true));
    }
  } else {
    // 뷰어엔드를 띄우는 경우가 아니면 앱에 현재 progress를 전달 해 준다.
    const endPageCount: number = yield call(getTalkViewerEndPageCount);
    window?.KNApp?.setProgress?.(
      Math.min(talkIndex, progress.totalTalkCount),
      //@ts-ignore
      progress.totalTalkCount,
      endPageCount
    );
    if (viewerEndState.isExposed) {
      yield put(setIsViewerEndExposed(false));
    }
  }
}

export function* showFullscreenWorker({
  payload,
}: ReturnType<typeof showFullscreen>) {
  const {talkId} = payload;
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);

  const {talkMap} = talkViewerState;
  const dataType = talkMap[talkId].datatype;
  // videoPlayType이 None인 경우 비디오의 풀스크린은 지원하지 않는다. 그래서 풀스크린이 일어나지 않은 것 처럼 바로 풀스크린을 닫아준다.
  if (
    (dataType === TalkDataType.FullscreenVideo ||
      dataType === TalkDataType.Movie) &&
    talkViewerState.videoPlayType === VideoPlayType.None
  ) {
    yield put(hideFullscreen());
    return;
  }
  yield put(setIsViewerMenuExposed({isExposed: false}));
  const mediaUrl = talkViewerState.talkMap[talkId]?.media?.source.url;
  // videoPlayType이 App인 경우에 풀스크린 동영상은 앱을 통해 실행한다.
  if (
    talkViewerState.videoPlayType === VideoPlayType.App &&
    (dataType === TalkDataType.FullscreenVideo ||
      dataType === TalkDataType.Movie) &&
    mediaUrl
  ) {
    if (window?.KNApp?.openVideoPlayer) {
      window.KNApp.openVideoPlayer(mediaUrl);
    }
    // 혹시나 앱에서 openVideoPlayer 함수를 지원 해 주지 않으면, 풀스크린 상태를 바로 종료시킨다.
    else {
      yield put(hideFullscreen());
    }
  }
}
// 씬 전환이 일어났을 때, 모든 미디어를 정지시킨다.
export function* changeSceneWorker() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  yield call(stopAllMedia);
  // 배경음이 등록되어있었다면 배경음도 해제한다
  if (talkViewerState.bgm) {
    yield put(unregisterBgm());
  }
}

export function* setMutedWorker({payload}: ReturnType<typeof setMuted>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  // 미디어를 직접 실행하는 경우, 배경음과 효과음은 음소거상태로 재생할 수 없어서 정지시킨다.
  if (talkViewerState.needMediaPlayedDirect) {
    if (talkViewerState.bgm) {
      yield put(stopMedia(talkViewerState.bgm.talkId));
    }
    if (talkViewerState.sound) {
      yield put(stopMedia(talkViewerState.sound.talkId));
    }
  }
  window?.KNApp?.setMuted?.(payload);
}

// biome-ignore lint/correctness/useYield: <explanation>
export function* talkViewerErrorOccuredWorker({
  payload,
}: ReturnType<typeof talkViewerErrorOccured>) {
  if (
    window.KNApp &&
    payload !== TalkViewerErrorType.AppError &&
    payload !== TalkViewerErrorType.NeedUpdate
  ) {
    if (window?.KNApp) {
      window.KNApp.finishViewerLoading?.();
      window.KNApp.setViewerError?.(
        tt(TALK_VIEWER_ERROR_MESSAGE_MAP[payload]),
        CLOSE_TALK_VIEWER_ERROR_TYPE_LIST.includes(payload)
      );
    }
  }
}

export function* setIsViewerMenuExposedWorker({
  payload: {isExposed, isInvokedByApp},
}: ReturnType<typeof setIsViewerMenuExposed>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  if (!isInvokedByApp) {
    if (isExposed) {
      window?.KNApp?.exposeViewerMenu?.();
    } else {
      window?.KNApp?.concealViewerMenu?.();
    }
  }
  if (isExposed && talkViewerState.settings.viewingMode === ViewingMode.Tap) {
    window.KNApp?.exposeScrollBar?.();
  }
}

// biome-ignore lint/correctness/useYield: <explanation>
export function* pauseAutoTapWorker() {
  window?.KNApp?.autoTapPaused?.();
}

// biome-ignore lint/correctness/useYield: <explanation>
export function* stopAutoTapWorker() {
  window?.KNApp?.autoTapStopped?.();
}

export function* setIsViewerEndExposedWorker({
  payload: isViewerEndExposed,
}: ReturnType<typeof setIsViewerEndExposed>) {
  window?.KPApp?.setIsViewerEndExposed?.(isViewerEndExposed);
  if (isViewerEndExposed) {
    yield put(setVisitedTalkCountToViewerEnd());
    yield call(stopAllMedia);
    const talkViewerState: TalkViewerState = yield select(
      selectTalkViewerState
    );
    if (!talkViewerState.currentProductId) {
      return;
    }
    if (talkViewerState.internalSettings.autoTap !== AutoTapType.Stop) {
      yield put(stopAutoTap());
    }

    const endPageCount: number = yield call(getTalkViewerEndPageCount);
    const product =
      talkViewerState.productInfoMap[talkViewerState.currentProductId];
    window?.KNApp?.setProgress?.(
      Math.min(
        product.progress.visitedTalkCount,
        product.progress.totalTalkCount
      ),
      // @ts-ignore
      product.progress.totalTalkCount,
      endPageCount
    );
  }
}

export function* playMediaWorker({
  payload: talkId,
}: ReturnType<typeof playMedia>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {bgm, sound, movie, talkMap, videoPlayType} = talkViewerState;
  const dataType = talkMap[talkId]?.datatype;
  // VideoPlayType 중 None 은 웹에서의 동영상 재생을 지원하지 않으므로 동영상의 경우 바로 미디어를 정지상태로 돌려준다.
  if (
    videoPlayType === VideoPlayType.None &&
    (dataType === TalkDataType.FullscreenVideo ||
      dataType === TalkDataType.Movie)
  ) {
    yield put(stopMedia(talkId));
    return;
  }
  // 동영상은 보는 도중 스크린이 꺼지면 안 되므로 앱의 sleep방지 함수를 호출 해 준다
  if (
    dataType === TalkDataType.FullscreenVideo ||
    dataType === TalkDataType.Movie
  ) {
    window?.KPApp?.disableScreenSleep?.();
  }
  // 다른 미디어들과 동시 재생 불가능한 케이스를 정리
  switch (dataType) {
    // 비디오류는 효과음, 배경음과 동시 재생이 불가능하므로 모두 정지시킨다
    case TalkDataType.FullscreenVideo:
    case TalkDataType.Movie: {
      if (bgm) {
        yield put(stopMedia(bgm.talkId));
      }
      if (sound) {
        yield put(stopMedia(sound.talkId));
      }
      break;
    }
    // 사운드류는 동영상만 정지시킨다 (효과음과 배경음은 서로 동시 재생이 가능하다)
    case TalkDataType.Sound:
    case TalkDataType.TalkEffect:
    case TalkDataType.Bgm: {
      if (movie) {
        yield put(stopMedia(movie.talkId));
      }
      break;
    }
  }
}

export function* stopMediaWorker({
  payload: talkId,
}: ReturnType<typeof stopMedia>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const dataType = talkViewerState.talkMap[talkId]?.datatype;
  // 동영상이 종료되면 다시 절전모드에 진입할 수 있도록 sleep방지를 해제해준다.
  // 오토탭 play상태에서는 절전모드에 진입하면 안 되므로 sleep방지를 따로 해제하지 않는다.
  if (
    (dataType === TalkDataType.FullscreenVideo ||
      dataType === TalkDataType.Movie) &&
    talkViewerState.internalSettings.autoTap !== AutoTapType.Play
  ) {
    window?.KPApp?.enableScreenSleep?.();
  }
}

export function* setExposedTalkWorker({
  payload,
}: ReturnType<typeof setExposedTalk>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {settings, talkMap, talkGroupMap, productInfoMap, currentProductId} =
    talkViewerState;
  const {bottomTalkId} = payload;
  if (!currentProductId) {
    return;
  }
  const product = productInfoMap[currentProductId];
  if (!product) {
    return;
  }

  const endPageCount: number = yield call(getTalkViewerEndPageCount);
  window?.KNApp?.setProgress?.(
    Math.min(
      product.progress.visitedTalkCount,
      product.progress.totalTalkCount
    ),
    // @ts-ignore
    product.progress.totalTalkCount,
    endPageCount
  );

  if (settings.viewingMode === ViewingMode.Scroll && bottomTalkId) {
    const bottomTalk = talkMap[bottomTalkId];
    const bottomTalkGroupId = bottomTalk.talkGroupId;
    const currentTalkGroup = talkGroupMap[bottomTalkGroupId];
    const firstBgm = currentTalkGroup.talkIds.find(
      id => talkMap[id].datatype === TalkDataType.Bgm
    );
    // 톡그룹내 배경음이 존재하는 경우, 해당 톡 그룹의 첫 배경음을 등록시킨다
    if (firstBgm) {
      yield put(registerBgm(firstBgm));
    }
    yield put(changeTalkGroup(bottomTalkGroupId));
  }
}

export function* setIsTutorialExposedWorker({
  payload: isTutorialExposed,
}: ReturnType<typeof setIsTutorialExposed>) {
  if (isTutorialExposed) {
    yield put(setIsViewerMenuExposed({isExposed: false}));
  }
  // 튜토리얼을 내릴 때 뷰어메뉴를 띄워준다.
  else {
    yield put(setIsViewerMenuExposed({isExposed: true}));
  }
}

export function* fetchTalkViewerContentRejectedWorker({
  payload,
}: ReturnType<
  | typeof fetchTalkViewerContent.rejected
  | typeof fetchTalkViewerContentFromAdminSite.rejected
  | typeof fetchTalkViewerContentFromCms.rejected
>) {
  if (!payload) {
    return;
  }
  const rejectReason = payload as TalkViewerErrorType;
  yield put(talkViewerErrorOccured(rejectReason));
}

export default function* talkViewerSaga() {
  yield all([
    takeEvery(
      fetchTalkViewerContentFromAdminSite.fulfilled,
      fetchTalkViewerContentFromAdminSiteFulfilledWorker
    ),
    takeEvery(
      fetchTalkViewerContentFromCms.fulfilled,
      fetchTalkViewerContentFromCmsFulfilledWorker
    ),
    takeEvery(
      fetchTalkViewerContent.fulfilled,
      fetchTalkViewerContentFulfilledWorker
    ),
    takeEvery(
      fetchTalkViewerContent.rejected,
      fetchTalkViewerContentRejectedWorker
    ),
    takeEvery(
      fetchTalkViewerContentFromAdminSite.rejected,
      fetchTalkViewerContentRejectedWorker
    ),
    takeEvery(
      fetchTalkViewerContentFromCms.rejected,
      fetchTalkViewerContentRejectedWorker
    ),
    takeEvery(talkViewerInfoFetched, talkViewerInfoFetchedWorker),
    takeLatest(playAutoTap, playAutoTapWorker),
    takeEvery(appendTalk, appendTalkWorker),
    takeEvery(changeViewingMode, changeViewingModeWorker),
    takeEvery(replayTalk, replayTalkWorker),
    takeEvery(setIsViewerMenuExposed, setIsViewerMenuExposedWorker),
    takeEvery(forceProcessToTalkIndex, forceProcessToTalkIndexWorker),
    takeEvery(
      talkViewerInfoLoadingFinished,
      talkViewerInfoLoadingFinishedWorker
    ),
    takeEvery(showFullscreen, showFullscreenWorker),
    takeEvery(changeScene, changeSceneWorker),
    takeEvery(setMuted, setMutedWorker),
    takeEvery(talkViewerErrorOccured, talkViewerErrorOccuredWorker),
    takeEvery(pauseAutoTap, pauseAutoTapWorker),
    takeEvery(stopAutoTap, stopAutoTapWorker),

    takeLeading(setIsViewerEndExposed, setIsViewerEndExposedWorker),
    takeEvery(playMedia, playMediaWorker),
    takeEvery(stopMedia, stopMediaWorker),
    takeEvery(setExposedTalk, setExposedTalkWorker),
    takeEvery(setIsTutorialExposed, setIsTutorialExposedWorker),
  ]);
}
